// src/components/notices/TerminationNoticeModal.jsx
import React, { useState } from 'react';
import { X, Download, FileText, CheckCircle, AlertCircle } from 'lucide-react';
import { useQuery } from 'react-query';
import { contractAccessService } from '../../services';
import toast from 'react-hot-toast';
import SignedDocumentUpload from '../contracts/SignedDocumentUpload';

const TerminationNoticeModal = ({ notice, token, contractInfo, onClose, onDownload }) => {
  const [activeTab, setActiveTab] = useState('info');

  // Fetch signed documents for this notice
  const { data: signedDocs, refetch: refetchDocs } = useQuery(
    ['noticeSignedDocs', token, notice.id],
    () => contractAccessService.getNoticeSignedDocuments(token, notice.id),
    {
      enabled: !!token && !!notice.id
    }
  );

  const handleUploadSuccess = (data) => {
    console.log('Upload successful for notice:', data);
    toast.success('Documente încărcate cu succes!');
    refetchDocs();
    // Optionally refresh parent data
    if (onClose) {
      // Could call a parent refresh here if needed
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b">
            <div>
              <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <AlertCircle className="w-6 h-6 text-red-600" />
                Notificare de Reziliere Contract
              </h2>
              <p className="text-sm text-gray-600">
                Contract {contractInfo.contractNumber} - {contractInfo.clientName}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b">
            <nav className="flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('info')}
                className={`py-3 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'info'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Informații
              </button>
              <button
                onClick={() => setActiveTab('upload')}
                className={`py-3 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'upload'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Încărcare Documente
              </button>
              <button
                onClick={() => setActiveTab('documents')}
                className={`py-3 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'documents'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Documente Încărcate ({signedDocs?.totalFiles || 0})
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 200px)' }}>
            {activeTab === 'info' && (
              <div className="p-6 space-y-4">
                {/* Alert Banner */}
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-red-900 mb-1">
                        Atenție: Notificare de Reziliere Contract
                      </h4>
                      <p className="text-sm text-red-800">
                        Acest document reprezintă o notificare oficială de încetare a contractului. 
                        Vă rugăm să citiți cu atenție și să confirmați primirea documentului.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Details Card */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold mb-3 text-gray-900">Detalii Reziliere</h3>
                  <dl className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <dt className="text-gray-600">Tip Document:</dt>
                      <dd className="font-medium text-gray-900">Notificare de Reziliere</dd>
                    </div>
                    <div className="flex justify-between text-sm">
                      <dt className="text-gray-600">Dată Emisiune:</dt>
                      <dd className="font-medium text-gray-900">
                        {new Date(notice.date).toLocaleDateString('ro-RO')}
                      </dd>
                    </div>
                    <div className="flex justify-between text-sm">
                      <dt className="text-gray-600">Status:</dt>
                      <dd>
                        {notice.hasSignedDocuments ? (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Confirmat / Semnat
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            În așteptare confirmare
                          </span>
                        )}
                      </dd>
                    </div>
                  </dl>
                </div>

                {/* Download Button */}
                <button
                  onClick={onDownload}
                  className="w-full bg-red-600 text-white py-3 px-4 rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Download className="w-5 h-5" />
                  Descarcă PDF Notificare de Reziliere
                </button>

                {/* Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-900 mb-2">Instrucțiuni:</h4>
                  <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                    <li>Descarcă notificarea de reziliere folosind butonul de mai sus</li>
                    <li>Citește cu atenție conținutul documentului</li>
                    <li>Semnează documentul pentru a confirma primirea (digital sau manual)</li>
                    <li>Dacă ai semnat manual, scanează documentul în PDF</li>
                    <li>Încarcă documentul semnat în tab-ul "Încărcare Documente"</li>
                  </ol>
                </div>

                {/* Additional Notice Info */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h4 className="font-medium text-yellow-900 mb-1 text-sm">
                    Notă Importantă
                  </h4>
                  <p className="text-xs text-yellow-800">
                    Încărcarea documentului semnat confirmă că ați primit și luat la cunoștință 
                    notificarea de reziliere. Pentru clarificări, contactați departamentul contracte.
                  </p>
                </div>
              </div>
            )}

            {activeTab === 'upload' && (
              <SignedDocumentUpload
                token={token}
                uploadType="notice"
                entityId={notice.id}
                entityLabel={`Notificare de Reziliere #${notice.id}`}
                onUploadSuccess={handleUploadSuccess}
              />
            )}

            {activeTab === 'documents' && (
              <div className="p-6">
                {signedDocs?.files && signedDocs.files.length > 0 ? (
                  <div className="space-y-2">
                    {signedDocs.files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                        <div className="flex items-center gap-2">
                          <FileText className="w-5 h-5 text-gray-500" />
                          <span className="text-sm font-medium">{file.fileName}</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <span className="text-xs text-gray-500">{file.fileExtension}</span>
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p className="mb-1">Niciun document încărcat încă</p>
                    <p className="text-sm">
                      Vă rugăm să încărcați notificarea semnată pentru confirmare
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Închide
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TerminationNoticeModal;